import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack, Typography } from '@mui/material';
import StyleConstants from '../../../core/styles.constants';
import { useMdiIcons } from '../hooks/use-mdi-icons';
import { getMdiIconPath, MDI_VIEWBOX_SIZE } from '../utils/mdi-icons';
import { layerPalette } from '../utils/editor-theme';
import DESK_ICON_SHORTLIST from '../utils/desk-icons';
import IconPicker from './icon-picker.component';

interface Props {
  /** The icon this is drawn as, or null for whatever it is drawn as without one. */
  readonly iconName: string | null;
  readonly onChange: (iconName: string | null) => void;
  /** What the control is labelled, since the same one sets a default and edits a selection. */
  readonly labelKey?: string;
  /**
   * What is being drawn, which decides what "no icon" means and looks like.
   *
   * A desk marker without an icon is the round dot; a piece of furniture without one is the
   * desk outline. Both need the way back to be named for what it actually restores — "plain
   * marker" on a desk image is an answer to a question nobody asked.
   */
  readonly kind?: 'desk' | 'furniture';
}

/**
 * Which built-in icon a bookable desk is drawn as.
 *
 * A round marker says where a seat is and nothing else, and a floor of identical dots is
 * unreadable the moment it holds more than one kind of thing — a standing desk, a phone
 * booth, a first-aid point. The icon is picked from the Material Design set the app already
 * ships, rather than uploaded: an image nobody else can resolve would render here and nowhere
 * else, and MDI is the one vocabulary both products already share.
 *
 * Drawn in the desk's own colour, not a colour of its own, so the icon still carries the one
 * thing the marker always carried: bookable, not bookable, or out of service.
 */
const DeskIconField = ({
  iconName, onChange, labelKey = 'floorplan-editor-desk-icon', kind = 'desk',
}: Props): React.JSX.Element => {
  const { t } = useTranslation();
  const [pickerOpen, setPickerOpen] = useState(false);
  const anchor = useRef<HTMLDivElement | null>(null);

  // Subscribed so the preview appears as soon as the set has loaded, rather than staying
  // blank until something else happens to re-render this panel.
  const iconsLoaded = useMdiIcons();
  const path = iconsLoaded ? getMdiIconPath(iconName) : undefined;

  return (
    <Stack spacing={1} ref={anchor}>
      <Typography variant="caption" color={StyleConstants.GREY_COLOR}>
        {t(labelKey)}
      </Typography>

      <Stack direction="row" spacing={1} alignItems="center">
        <Box
          sx={{
            width: 36,
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: `1px solid ${StyleConstants.LIGHT_GREY_COLOR}`,
            borderRadius: 1,
          }}
        >
          {/* Whatever it will actually be drawn as, rather than an empty box: the marker for
              a desk, and the desk outline for a piece of furniture. */}
          {path && (
            <svg width={22} height={22} viewBox={`0 0 ${MDI_VIEWBOX_SIZE} ${MDI_VIEWBOX_SIZE}`}>
              <path d={path} fill={kind === 'desk' ? layerPalette.desk : layerPalette.deskShapeEdge} />
            </svg>
          )}

          {!path && kind === 'desk' && (
            <svg width={22} height={22} viewBox="0 0 22 22">
              <circle cx={11} cy={11} r={6} fill={layerPalette.desk} stroke={layerPalette.deskEdge} />
            </svg>
          )}

          {!path && kind === 'furniture' && (
            // The C the desk outline actually is, at the proportions `symbolRing` draws it in.
            <svg width={24} height={18} viewBox="0 0 24 18">
              <path
                d="M1 1 H23 V17 H18 V6 H6 V17 H1 Z"
                fill={layerPalette.deskShape}
                stroke={layerPalette.deskShapeEdge}
              />
            </svg>
          )}
        </Box>

        <Button size="small" onClick={(): void => setPickerOpen(true)} sx={{ textTransform: 'none' }}>
          {t(iconName ? 'floorplan-editor-desk-icon-change' : 'floorplan-editor-desk-icon-choose')}
        </Button>

        {iconName && (
          <Button size="small" onClick={(): void => onChange(null)} sx={{ textTransform: 'none' }}>
            {t(kind === 'desk'
              ? 'floorplan-editor-desk-icon-clear'
              : 'floorplan-editor-desk-image-clear')}
          </Button>
        )}
      </Stack>

      {/* A name that is not in the set draws the plain marker rather than nothing, so it is
          worth saying out loud — otherwise the desk simply looks like it was never changed. */}
      {iconName && iconsLoaded && !path && (
        <Typography variant="caption" color={StyleConstants.PINK_COLOR}>
          {t('floorplan-editor-icon-unknown')}
        </Typography>
      )}

      <IconPicker
        anchorEl={pickerOpen ? anchor.current : null}
        suggested={DESK_ICON_SHORTLIST}
        onClose={(): void => setPickerOpen(false)}
        onPick={(name): void => {
          onChange(name);
          setPickerOpen(false);
        }}
      />
    </Stack>
  );
};

export default DeskIconField;
